'use client';

import { SalesforceService } from '@/lib/Services/SalesforceService';
import { fieldRules } from '@/utils/form';
import { ProspectData } from '@/types/salesforce';
import { conversionContext } from '@/utils/conversionContext';
import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import NextButton from '../Button/NextButton';
import Input from '../Input/Input';
import EngineLoading from '../Loading/EngineLoading';
import styles from './EngineLandingModal.module.scss';

interface FormValues {
  email: string;
}

const tcpa = `By clicking the “Click to continue with your offer” button above, you are providing express written consent for Pennie Mgmt LLC its partners, and ReservedMyLoan LLC. This applies even if you're on a Do-Not-Call list. Consent isn’t required to make a purchase. Msg/data rates may apply. You also agree to our Terms and Privacy Policy. Text HELP for help or STOP to cancel.`;

interface Props {
  prospect?: ProspectData | null;
}

const EngineLandingModal = ({ prospect }: Props) => {
  const { id } = useParams();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    defaultValues: { email: prospect?.Email__c || '' },
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [toggledRemark, setToggledRemark] = useState(false);
  const [redirectValue, setRedirectValue] = useState('');

  const onSubmit: SubmitHandler<FormValues> = async () => {
    setLoading(true);
    setError(false);
    if (prospect) {
      try {
        const submissionId = await SalesforceService.convertProspect(
          id as string,
          {
            variant: 'engine',
            tcpa,
            ...(await conversionContext()),
          }
        );
        if (!submissionId) throw new Error('No submission for prospect');
        setRedirectValue(`/booking/portal/${submissionId}?prospectId=${id}`);
        await SalesforceService.updateProspect(id as string, {
          IsCompletedSteps__c: true,
        });
        setLoading(false);
      } catch {
        setError(true);
        setLoading(false);
      }
    } else {
      setError(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (prospect && prospect.Email__c) {
      setValue('email', prospect.Email__c);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospect]);

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.titleWrapper}>
          <p className={styles.title}>Time to review your offer</p>
          <p className={styles.subtitle}>
            Please confirm your email below and click the button to proceed.
          </p>
        </div>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <Input
            inputMode="email"
            id="email"
            label="Email"
            placeholder="Your email"
            type="email"
            register={register('email', fieldRules.email)}
            error={errors.email}
          />
          <NextButton
            type="button"
            buttonType="submit"
            text="Click to continue with my offer"
            styles={styles.button}
            disabled={loading || !prospect}
          />
          {error && (
            <p className={styles.error}>
              Something went wrong.
              <br />
              Try again later.
            </p>
          )}
        </form>
        <div className={styles.remarkWrapper}>
          <p
            className={`${styles.remark} ${toggledRemark ? '' : styles.hiddenRemark}`}
          >
            By clicking the “Click to continue with your offer” button above,
            you are providing express written consent for Pennie Mgmt LLC its
            partners, and ReservedMyLoan LLC. This applies even if you&apos;re
            on a Do-Not-Call list. Consent isn&apos;t required to make a
            purchase. Msg/data rates may apply. You also agree to our{' '}
            <a
              target="_blank"
              href="https://reservemyloan.com/privacy/terms-of-service"
              rel="noreferrer"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              target="_blank"
              href="https://reservemyloan.com/privacy/online-privacy-policy"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            .
            <br />
            Text HELP for help or text STOP to cancel.
          </p>
          <button
            onClick={() => setToggledRemark((prev) => !prev)}
            type="button"
            className={styles.remarkBtn}
          >
            {toggledRemark ? 'Show less' : 'Show more'}
          </button>
        </div>
      </div>
      <EngineLoading redirectValue={redirectValue} loading={loading} />
    </>
  );
};

export default EngineLandingModal;
